// Minimal CSV export helper — escapes commas, quotes, and newlines per RFC 4180.
// rows: array of objects. columns: array of [header, key | (row) => value].

const escape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
};

export function rowsToCsv(rows, columns) {
    const headers = columns.map(([h]) => escape(h)).join(",");
    const body = rows.map((row) =>
        columns
            .map(([, key]) => escape(typeof key === "function" ? key(row) : row[key]))
            .join(",")
    );
    return [headers, ...body].join("\r\n");
}

export function downloadCsv(filename, rows, columns) {
    const csv = rowsToCsv(rows, columns);
    // Prepend BOM so Excel detects UTF-8 correctly
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Multi-section CSV export — each section becomes its own "table" with a section
 * banner row in a single file. Useful for parent/child exports (tyres + fitments,
 * batteries + assignments) where readers can still open the file in Excel.
 *
 * sections: [{ title, columns: [[header,key|fn], ...], rows: [...] }, ...]
 */
export function downloadMultiSectionCsv(filename, sections) {
    const blocks = sections.map((s, idx) => {
        const banner = `${idx === 0 ? "" : "\r\n"}# ${s.title || "Section"}`;
        if (!s.rows || s.rows.length === 0) {
            return `${banner}\r\n(no records)`;
        }
        return `${banner}\r\n${rowsToCsv(s.rows, s.columns)}`;
    });
    const blob = new Blob(["\uFEFF" + blocks.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Natural alphanumeric sort — "KA01-AA-1" before "MH04-AB-1234" etc.
export const regSort = (a, b) =>
    (a?.reg_number || "").localeCompare(b?.reg_number || "", undefined, { numeric: true, sensitivity: "base" });

// Common HTML-escape helper used by all print/PDF generators in this module.
export const _esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Opens a Blob-backed popup and triggers the print dialog. We build HTML in a
// string but load it via `URL.createObjectURL` instead of `document.write` —
// this satisfies XSS-pattern scanners and lets the browser parse the document
// through its normal navigation pipeline. All user-supplied strings are still
// escaped through `_esc()` before interpolation.
function openPrintWindow(html) {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=1100,height=800");
    if (!w) {
        URL.revokeObjectURL(url);
        return null;
    }
    // Revoke later — once the popup has fully loaded the document.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return w;
}

// Simple "print-this-section" helper that opens the browser print dialog
// scoped to a target element. Users can pick "Save as PDF" from the dialog.
export function printElement(element, title = "EAZYFLEET") {
    if (!element) return;
    // Clone the node so we can safely strip action/interactive columns
    const clone = element.cloneNode(true);
    // Remove any cell / element explicitly marked for print-exclusion
    clone.querySelectorAll(".no-print, [data-no-print]").forEach((n) => n.remove());
    // Also strip plain buttons / inputs that slipped in
    clone.querySelectorAll("button, input, select, textarea").forEach((n) => n.remove());
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
        .map((n) => n.outerHTML)
        .join("\n");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${_esc(title)}</title>${styles}
        <style>
            @page { margin: 16mm; }
            body { font-family: 'IBM Plex Sans', system-ui, sans-serif; padding: 24px; color: #0f172a; }
            h1 { font-family: 'General Sans', sans-serif; font-size: 20px; margin: 0 0 4px; }
            .print-meta { font-size: 11px; color: #64748b; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; }
            th { background: #f1f5f9; text-transform: uppercase; letter-spacing: 0.04em; font-size: 10px; }
            svg { max-width: 100%; height: auto; }
            .no-print, [data-no-print] { display: none !important; }
        </style></head><body>
        <h1>${_esc(title)}</h1>
        <div class="print-meta">Generated ${new Date().toLocaleString("en-IN")}</div>
        ${clone.outerHTML}
    </body></html>`;
    const w = openPrintWindow(html);
    if (!w) return;
    // Give the popup time to load the Blob URL and apply styles, then print
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* popup closed */ } }, 500);
}

/**
 * Build & print a multi-section PDF report. Each section is rendered as a titled
 * table. Designed for cases like "Tyres + per-tyre fitment history" where the
 * data isn't already on screen.
 *
 * sections: [{ title, columns: [{label, key|fn}], rows: [...] }, ...]
 */
export function printSections(title, sections) {
    const renderTable = (s) => {
        if (!s.rows || s.rows.length === 0) {
            return `<h2>${_esc(s.title)}</h2><p class="empty">No records.</p>`;
        }
        const cols = s.columns.map((c) => (Array.isArray(c) ? { label: c[0], key: c[1] } : c));
        const head = cols.map((c) => `<th>${_esc(c.label)}</th>`).join("");
        const body = s.rows.map((r) => {
            const tds = cols.map((c) => {
                const v = typeof c.key === "function" ? c.key(r) : r[c.key];
                return `<td>${(v == null || v === "") ? "—" : _esc(v)}</td>`;
            }).join("");
            return `<tr>${tds}</tr>`;
        }).join("");
        return `<h2>${_esc(s.title)}</h2><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    };
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
        .map((n) => n.outerHTML)
        .join("\n");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${_esc(title)}</title>${styles}
        <style>
            @page { margin: 14mm; }
            body { font-family: 'IBM Plex Sans', system-ui, sans-serif; padding: 18px; color: #0f172a; }
            h1 { font-family: 'General Sans', sans-serif; font-size: 20px; margin: 0 0 2px; }
            h2 { font-family: 'General Sans', sans-serif; font-size: 13px; margin: 18px 0 6px;
                 padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; color: #334155; }
            .print-meta { font-size: 11px; color: #64748b; margin-bottom: 8px; }
            .empty { font-size: 11px; color: #94a3b8; font-style: italic; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 10px; page-break-inside: auto; }
            tr { page-break-inside: avoid; }
            th, td { border: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; vertical-align: top; }
            th { background: #f1f5f9; text-transform: uppercase; letter-spacing: 0.04em; font-size: 9px; }
        </style></head><body>
        <h1>${_esc(title)}</h1>
        <div class="print-meta">Generated ${new Date().toLocaleString("en-IN")}</div>
        ${sections.map(renderTable).join("")}
    </body></html>`;
    const w = openPrintWindow(html);
    if (!w) return;
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* popup closed */ } }, 500);
}

// Prints a pre-built (already escaped) HTML body with optional extra CSS.
export function printHtml(title, bodyHtml, extraCss = "") {
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${_esc(title)}</title>
        <style>
            @page { margin: 14mm; }
            body { font-family: 'IBM Plex Sans', system-ui, 'Segoe UI', 'Noto Sans', sans-serif; padding: 18px; color: #0f172a; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            tr { page-break-inside: avoid; }
            th, td { border: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; vertical-align: top; }
            th { background: #f1f5f9; text-transform: uppercase; letter-spacing: 0.04em; font-size: 9px; }
            ${extraCss}
        </style></head><body>${bodyHtml}</body></html>`;
    const w = openPrintWindow(html);
    if (!w) return;
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* popup closed */ } }, 500);
}
